<template>
  <div ref="rootRef" class="lang-switcher" :class="{ 'reserves-nav-height': placement === 'bottom' }"
       @pointerenter="onPointerEnter"
       @mouseleave="open = false"
       @focusout="onFocusOut"
       @keydown.esc="closeAndRefocus">
    <button ref="triggerRef" type="button" class="language-toggler" :class="{ open }"
            :aria-label="$t('chooseLanguage')"
            aria-haspopup="listbox"
            :aria-expanded="open"
            @click="open = !open"
            @keydown.down.prevent="openAt(0)"
            @keydown.up.prevent="openAt(-1)">
      <Icon name="ph:globe-light" preload size="22px"/>
      <span class="current-language">{{ $t('currentLanguage') }}</span>
      <Icon name="ph:caret-down-bold" size="12px" class="caret" aria-hidden="true"/>
    </button>
    <Transition name="lang-pop">
      <div v-show="open" ref="panelRef" class="dropdown-content" :class="placement"
           role="listbox" :aria-label="$t('chooseLanguage')">
        <NuxtLink v-for="localeOption in orderedLocales" :key="localeOption.code"
                  :to="switchLocalePath(localeOption.code)"
                  replace
                  class="language-selector"
                  :class="{ active: localeOption.code === locale }"
                  role="option"
                  :aria-selected="localeOption.code === locale"
                  :lang="localeOption.code"
                  @click="open = false"
                  @keydown.down.prevent="move(1)"
                  @keydown.up.prevent="move(-1)">
          <span>{{ localeOption.name }}</span>
          <Icon v-if="localeOption.code === locale" name="ph:check-bold" size="14px" aria-hidden="true"/>
        </NuxtLink>
      </div>
    </Transition>
  </div>
</template>

<script setup>
defineProps({
  // The panel is absolutely positioned, so it has to open away from the edge
  // it sits near: downward in the desktop nav, upward in the mobile menu
  // footer, where opening down would cover the footer image and overflow the
  // bottom of the sheet.
  placement: {
    type: String,
    default: 'bottom',
    validator: value => ['bottom', 'top'].includes(value)
  }
});

const {locale, locales, defaultLocale} = useI18n();
const switchLocalePath = useSwitchLocalePath();

// `locales` comes straight from nuxt.config.ts, where the array happens to
// start with English. The order shown is a decision rather than an accident:
// this is a Greek refuge and `el` is the default locale, so Greek leads and the
// rest keep the config order. Same ordering as pages/services/menu.vue.
const orderedLocales = computed(() => [
  ...locales.value.filter(option => option.code === defaultLocale),
  ...locales.value.filter(option => option.code !== defaultLocale),
]);

// The dropdown used to open on :hover alone, which left it unreachable by
// keyboard: its links are display:none until the panel opens, so they could
// never take focus to open it. Driving it from state keeps the pointer
// behaviour identical while making the trigger operable.
const open = ref(false);
const rootRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);

// Touch browsers fire the enter event just before the click, so hovering and
// clicking would open the panel and immediately close it again -- leaving it
// unopenable by tap. Only a real mouse gets hover-to-open; a tap falls through
// to the click handler and toggles.
function onPointerEnter(event) {
  if (event.pointerType === 'mouse') open.value = true;
}

// mouseleave covers the mouse; a touch pointer never leaves, so an outside tap
// is what dismisses the panel there.
function onPointerDownOutside(event) {
  if (open.value && !rootRef.value?.contains(event.target)) open.value = false;
}

onMounted(() => document.addEventListener('pointerdown', onPointerDownOutside));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDownOutside));

// Only close once focus has actually left the whole switcher, so tabbing from
// the trigger into the language links does not shut the panel.
function onFocusOut(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) open.value = false;
}

function closeAndRefocus() {
  if (!open.value) return;
  open.value = false;
  triggerRef.value?.focus();
}

function options() {
  return Array.from(panelRef.value?.querySelectorAll('.language-selector') ?? []);
}

// Arrow keys on the trigger open the panel and land on an end of the list;
// arrow keys inside it walk the options and wrap, the usual listbox contract.
function openAt(index) {
  open.value = true;
  nextTick(() => {
    const items = options();
    items.at(index)?.focus();
  });
}

function move(step) {
  const items = options();
  const current = items.indexOf(document.activeElement);
  if (current === -1) return;
  items[(current + step + items.length) % items.length]?.focus();
}
</script>

<style scoped lang="sass">
.lang-switcher
  position: relative
  display: inline-flex
  align-items: center
  width: fit-content
  font-size: 20px

// The switcher is the tallest item in the desktop nav bar, so it is what sets
// the bar's height and therefore the hash-scroll offsets hardcoded in
// app/router.options.ts. The trigger inside is now a compact pill rather than
// a label carrying 1em block margins, so this reproduces the height that label
// occupied (5px padding + 20px margin + line box, top and bottom) and the nav
// does not resize. The mobile menu has no such constraint and just hugs the
// pill.
.lang-switcher.reserves-nav-height
  min-height: 74px

.language-toggler
  display: flex
  align-items: center
  gap: 0.5rem
  padding: 0.5rem 0.85rem
  border: 1px solid transparent
  border-radius: $border-radius-btn
  background: none
  color: inherit
  font: inherit
  font-size: 1rem
  line-height: 1.2
  cursor: pointer
  transition: background-color $transition-fast, border-color $transition-fast, color $transition-fast

  &:hover,
  &.open
    background-color: rgba(0, 0, 0, 0.04)
    border-color: rgba(0, 0, 0, 0.08)

  &:focus-visible
    outline: 2px solid $color-primary
    outline-offset: 2px

.caret
  transition: transform $transition-fast
  opacity: 0.6

.language-toggler.open .caret
  transform: rotate(180deg)

.dropdown-content
  position: absolute
  left: 50%
  transform: translate(-50%, 0)
  z-index: 2
  display: flex
  flex-direction: column
  gap: 2px
  padding: 6px
  width: max-content
  min-width: 11ch
  background-color: $color-bg-glass
  backdrop-filter: $blur-standard
  border: 1px solid rgba(255, 255, 255, 0.3)
  border-radius: $border-radius-card
  box-shadow: $shadow-modern

  // The nav-height reservation is empty space below the compact trigger, so a
  // small offset would leave the panel overlapping the nav bar's bottom edge.
  // 1rem clears the bar's own 0.75rem padding; the mobile menu has no such
  // edge to clear and just sits above the trigger.
  &.bottom
    top: calc(100% + 1rem)

  &.top
    bottom: calc(100% + 0.5rem)

.language-selector
  display: flex
  align-items: center
  justify-content: space-between
  gap: 1rem
  padding: 0.6rem 0.85rem
  border-radius: $border-radius-btn
  background-color: transparent
  color: $color-text-main
  font-size: 1rem
  text-decoration: none
  white-space: nowrap
  transition: background-color $transition-fast, color $transition-fast

  &:hover
    background-color: rgba(0, 0, 0, 0.06)

  &:focus-visible
    outline: 2px solid $color-primary
    outline-offset: -2px

  &.active
    color: $color-primary
    font-weight: 600
    background-color: rgba($color-primary, 0.12)

// Slide the panel out of the trigger: away from it when opening downward,
// up out of it when opening above.
.lang-pop-enter-active,
.lang-pop-leave-active
  transition: opacity 200ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1)

.lang-pop-enter-from.bottom,
.lang-pop-leave-to.bottom
  opacity: 0
  transform: translate(-50%, -0.5rem)

.lang-pop-enter-from.top,
.lang-pop-leave-to.top
  opacity: 0
  transform: translate(-50%, 0.5rem)

.dark-mode .language-toggler
  color: lightgray

  &:hover,
  &.open
    background-color: rgba(255, 255, 255, 0.08)
    border-color: rgba(255, 255, 255, 0.12)

.dark-mode .dropdown-content
  background-color: $color-bg-glass-dark
  border-color: rgba(255, 255, 255, 0.1)

.dark-mode .language-selector
  color: $color-text-light

  &:hover
    background-color: rgba(255, 255, 255, 0.1)

  &.active
    color: $color-primary
    background-color: rgba($color-primary, 0.18)
</style>
